package com.leaguetennis.shared.model

import kotlinx.serialization.Serializable

@Serializable
enum class PlayerStatus {
    ACTIVE,
    INACTIVE
}

@Serializable
data class Player(
    val id: String,
    val name: String,
    val email: String,
    val singlesRanking: Int = 1000,
    val doublesRanking: Int = 1000,
    val leagueRankings: Map<String, Int> = emptyMap(),
    val status: PlayerStatus = PlayerStatus.ACTIVE
)

@Serializable
enum class LeagueType {
    SINGLES,
    DOUBLES
}

@Serializable
enum class LeagueStatus {
    ACTIVE,
    INACTIVE
}

@Serializable
data class League(
    val id: String,
    val name: String,
    val description: String,
    val type: LeagueType = LeagueType.SINGLES,
    val status: LeagueStatus = LeagueStatus.ACTIVE
)

@Serializable
data class LeagueSession(
    val leagueId: String,
    val date: String, //YYYY-MM-DD
    val time: String, //HH:MM
    val attendingPlayerIds: List<String>,
    val matchIds: List<String> = emptyList()
)

@Serializable
data class PlayerStats(
    val playerId: String,
    val leagueId: String,
    val ranking: Int,
    val matchesWon: Int,
    val matchesLost: Int,
    val setsWon: Int,
    val setsLost: Int,
    val gamesWon: Int,
    val gamesLost: Int,
    val tieBreaksWon: Int,
    val tieBreaksLost: Int
)

@Serializable
data class MatchScore(
    val sets: List<MatchSet>
) {
    fun swapSides(): MatchScore = MatchScore(sets.map { it.swapSides() })

    override fun toString(): String = sets.joinToString(" ") { set ->
        val games = "${set.side1Games}-${set.side2Games}"
        if (set.side1TieBreak != null || set.side2TieBreak != null) {
            "$games(${set.side1TieBreak ?: 0}-${set.side2TieBreak ?: 0})"
        } else {
            games
        }
    }

    companion object {
        private val setRegex = Regex("""(\d+)-(\d+)(?:\((\d+)-(\d+)\))?""")

        fun fromString(scoreStr: String): MatchScore {
            if (scoreStr.isBlank()) return MatchScore(emptyList())

            if (scoreStr.contains(",")) {
                throw IllegalArgumentException("Score should use spaces between sets instead of commas")
            }
            
            val sets = scoreStr.split(" ").filter { it.isNotBlank() }.map { setStr ->
                val match = setRegex.matchEntire(setStr.trim())
                    ?: throw IllegalArgumentException("Invalid set format: $setStr")
                
                val side1Games = match.groupValues[1].toInt()
                val side2Games = match.groupValues[2].toInt()
                val side1TieBreak = match.groupValues[3].takeIf { it.isNotEmpty() }?.toInt()
                val side2TieBreak = match.groupValues[4].takeIf { it.isNotEmpty() }?.toInt()

                if (side1Games > 7 || side2Games > 7) {
                    throw IllegalArgumentException("Games in a set cannot exceed 7: $setStr")
                }

                MatchSet(side1Games, side2Games, side1TieBreak, side2TieBreak)
            }
            return MatchScore(sets)
        }
    }
}

@Serializable
data class MatchSet(
    val side1Games: Int,
    val side2Games: Int,
    val side1TieBreak: Int? = null,
    val side2TieBreak: Int? = null
) {
    fun swapSides(): MatchSet = MatchSet(
        side1Games = side2Games,
        side2Games = side1Games,
        side1TieBreak = side2TieBreak,
        side2TieBreak = side1TieBreak
    )
}

@Serializable
enum class MatchWinner {
    TIE,
    SIDE1,
    SIDE2
}

@Serializable
data class ScoreUpdateRequest(val score: String, val winnerSide: Int)

@Serializable
sealed interface Match {
    val id: String
    val leagueId: String
    val score: MatchScore?
    val winnerSide: MatchWinner?
    val date: String
}

@Serializable
data class SinglesMatch(
    override val id: String,
    override val leagueId: String,
    val player1Id: String,
    val player2Id: String,
    override val score: MatchScore? = null,
    override val winnerSide: MatchWinner? = null,
    override val date: String
) : Match

@Serializable
data class DoublesMatch(
    override val id: String,
    override val leagueId: String,
    val team1Player1Id: String,
    val team1Player2Id: String,
    val team2Player1Id: String,
    val team2Player2Id: String,
    override val score: MatchScore? = null,
    override val winnerSide: MatchWinner? = null,
    override val date: String
) : Match

@Serializable
data class UserSession(val id: String, val name: String, val email: String, val status: PlayerStatus)
